import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { listSubscriptions, PaginatedSubscriptionsResponse, SubscriptionListItem } from "@/services/subscriptionsApi";
import { subscriptionBadgeColor, subscriptionBadgeLabel } from "@/lib/subscriptionBadge";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import { roleBadgeLabel } from "@/lib/roleBadge";

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedSubscriptionsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listSubscriptions(navigate, { page, size });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size]);

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  const typeBadge = (type: string | null | undefined) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${subscriptionBadgeColor(type)}`}>
      {subscriptionBadgeLabel(type)}
    </span>
  );

  // Badge tier B2B (Organization.tier) — affiché uniquement pour un compte business.
  const b2bBadge = (s: SubscriptionListItem) =>
    s.is_business && s.organization_tier ? (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${tierBadgeColor(s.organization_tier)}`}>
        {tierBadgeLabel(s.organization_tier)}
      </span>
    ) : (
      <span className="text-gray-400">—</span>
    );

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Abonnements</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Liste des abonnements</h2>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select className="border rounded px-2 py-1 text-sm" value={size} onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">User ID</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">B2B</th>
                  <th className="px-3 py-2 text-left">Organisation</th>
                  <th className="px-3 py-2 text-left">Stripe</th>
                  <th className="px-3 py-2 text-left">Invitations aujourd'hui</th>
                  <th className="px-3 py-2 text-left">Messages aujourd'hui</th>
                  <th className="px-3 py-2 text-left">Inscription</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="px-3 py-2">{s.id}</td>
                    <td className="px-3 py-2 font-mono text-xs">{s.user_id.substring(0, 12)}...</td>
                    <td className="px-3 py-2">{typeBadge(s.subscription_type)}</td>
                    <td className="px-3 py-2">{b2bBadge(s)}</td>
                    <td className="px-3 py-2">
                      {s.organization_name ? (
                        <div>
                          {s.organization_id ? (
                            <Link to={`/organizations/${s.organization_id}`} className="text-blue-600 hover:underline">
                              {s.organization_name}
                            </Link>
                          ) : (
                            s.organization_name
                          )}
                          {s.member_role && (
                            <div className="text-xs text-gray-500">{roleBadgeLabel(s.member_role)}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{s.stripe_customer_id || "-"}</td>
                    <td className="px-3 py-2">{s.invites_sent_today ?? 0}</td>
                    <td className="px-3 py-2">{s.messages_sent_today ?? 0}</td>
                    <td className="px-3 py-2">{s.registration_date ? new Date(s.registration_date).toLocaleDateString() : "-"}</td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/subscriptions/${encodeURIComponent(s.user_id)}`}
                        className="text-blue-600 hover:underline"
                      >
                        Voir
                      </Link>
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={10}>Aucun abonnement</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
